// Collection page scroll memory manager
class CollectionScrollMemory {
  constructor() {
    this.storageKey = 'collection_scroll_state';
    this.isRestoring = false;

    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    // Event dispatcher from theme (fallback safe)
    this.dispatchEvent = window.r$1 || function (event) {
      document.dispatchEvent(new CustomEvent(event));
    };

    this.init();
  }

  init() {
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) {
        // Page restored from bfcache; scroll is already correct.
        this.clearState();
      }
    });

    if (this.shouldRestore()) {
      this.isRestoring = true;
      window.__collectionScrollRestoring = true;
      this.showPreloader();
      requestAnimationFrame(() => {
        setTimeout(() => this.restorePosition(), 200);
      });
    }

    this.trackProductClicks();
    this.trackBreadcrumbClicks();

    window.addEventListener('pagehide', () => {
      if (!this.isProductNavigation && !this.isBreadcrumbNavigation) {
        this.clearState();
      }
    });
  }

  trackProductClicks() {
    document.addEventListener('click', (e) => {
      const productLink = e.target.closest('a[href*="/products/"]');
      if (!productLink) return;

      this.isProductNavigation = true;

      const stateData = {
        scrollY: window.scrollY,
        productHandle: this.extractHandle(productLink.href),
        page: this.getCurrentPage(),
        timestamp: Date.now(),
        url: window.location.href,
        fromCollection: true,
        navigationType: 'product'
      };

      this.saveState(stateData);
    });
  }

  trackBreadcrumbClicks() {
    // Use event delegation since breadcrumbs might be added dynamically
    document.addEventListener('click', (e) => {
      const breadcrumbLink = e.target.closest('a.breadcrumbs__link[href*="/collections/"]');
      if (!breadcrumbLink) return;

      // Only track if clicking a breadcrumb that points to current collection
      const breadcrumbUrl = new URL(breadcrumbLink.href);
      const currentUrl = new URL(window.location.href);

      if (breadcrumbUrl.pathname === currentUrl.pathname) {
        this.isBreadcrumbNavigation = true;

        const stateData = {
          scrollY: window.scrollY,
          productHandle: null,
          page: this.getCurrentPage(),
          timestamp: Date.now(),
          url: window.location.href,
          fromCollection: true,
          navigationType: 'breadcrumb'
        };

        this.saveState(stateData);
      }
    });
  }

  saveState(data) {
    sessionStorage.setItem(this.storageKey, JSON.stringify(data));
  }

  getState() {
    const data = sessionStorage.getItem(this.storageKey);
    return data ? JSON.parse(data) : null;
  }

  clearState() {
    sessionStorage.removeItem(this.storageKey);
  }

  shouldRestore() {
    const state = this.getState();
    if (!state) return false;

    const isRecent = Date.now() - state.timestamp < 300000;
    const fromProduct = document.referrer.includes('/products/');
    const fromCollection = document.referrer.includes('/collections/');
    const isBackNavigation =
      window.performance?.navigation?.type === 2 ||
      window.performance?.getEntriesByType?.('navigation')?.[0]?.type === 'back_forward';

    return (
      isRecent &&
      state.url === window.location.href &&
      state.fromCollection &&
      (fromProduct || fromCollection || isBackNavigation)
    );
  }

  async restorePosition() {
    const state = this.getState();
    if (!state) {
      this.finishRestoring();
      return;
    }

    // If coming from breadcrumb, just restore scroll position (no specific product)
    if (state.navigationType === 'breadcrumb') {
      await this.restoreScrollOnly(state);
      return;
    }

    // Check if the target product is already in the first loaded page
    const targetProduct = document.querySelector(
      `[data-identify-product-handle="${state.productHandle}"]`
    );

    if (targetProduct) {
      // Wait one frame for any pending layout work before measuring position
      await new Promise(r => requestAnimationFrame(r));
      this.scrollToProduct(targetProduct);
      this.finishRestoring();
      return;
    }

    const ajaxinateButton = document.querySelector('.st__infinite-trigger a');
    if (!ajaxinateButton) {
      // No infinite scroll present; best-effort fallback
      window.scrollTo({ top: state.scrollY, behavior: 'instant' });
      this.finishRestoring();
      return;
    }

    await this.loadUntilVisible(state);
  }

  // Called once at the end of any restoration path.
  finishRestoring() {
    this.isRestoring = false;
    window.__collectionScrollRestoring = false;
    window.dispatchEvent(new CustomEvent('collectionScrollRestored'));
    this.hidePreloader();
    this.clearState();
  }

  async restoreScrollOnly(state) {
    const maxAttempts = Math.min(state.page, 15);
    let attempts = 0;

    while (attempts < maxAttempts - 1) {
      const ajaxinateButton = document.querySelector('.st__infinite-trigger a');
      if (!ajaxinateButton) break;

      const isDisabled =
        ajaxinateButton.classList.contains('is-disabled') ||
        ajaxinateButton.hasAttribute('disabled') ||
        ajaxinateButton.style.display === 'none' ||
        ajaxinateButton.offsetParent === null;

      if (isDisabled) break;

      const nextPageUrl = ajaxinateButton.href;
      if (!nextPageUrl) break;

      try {
        const response = await fetch(nextPageUrl);
        const html = await response.text();

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newContainer = doc.querySelector('.st__infinite-container');
        const newPagination = doc.querySelector('.st__infinite-trigger');

        if (!newContainer) break;

        const currentContainer = document.querySelector('.st__infinite-container');
        if (currentContainer && newContainer.innerHTML.trim()) {
          currentContainer.insertAdjacentHTML('beforeend', newContainer.innerHTML);
        }

        const paginationContainer = document.querySelector('.st__infinite-trigger');
        if (paginationContainer && newPagination) {
          paginationContainer.innerHTML = newPagination.innerHTML;
        } else {
          break;
        }

        await new Promise((r) => setTimeout(r, 200));

        window.ajaxinatePage++;
        this.dispatchEvent('collection:updated');

        attempts++;
      } catch {
        break;
      }
    }

      window.scrollTo({ top: state.scrollY, behavior: 'instant' });
    this.finishRestoring();
  }

  async loadUntilVisible(state) {
    const maxAttempts = Math.min(state.page + 2, 15);
    let attempts = 0;

    while (attempts < maxAttempts) {
      const ajaxinateButton = document.querySelector('.st__infinite-trigger a');
      if (!ajaxinateButton) break;

      const isDisabled =
        ajaxinateButton.classList.contains('is-disabled') ||
        ajaxinateButton.hasAttribute('disabled') ||
        ajaxinateButton.style.display === 'none' ||
        ajaxinateButton.offsetParent === null;

      if (isDisabled) break;

      const nextPageUrl = ajaxinateButton.href;
      if (!nextPageUrl) break;

      const productCountBefore = document.querySelectorAll('.product-item').length;

      try {
        const response = await fetch(nextPageUrl);
        const html = await response.text();

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newContainer = doc.querySelector('.st__infinite-container');
        const newPagination = doc.querySelector('.st__infinite-trigger');

        if (!newContainer) break;

        const currentContainer = document.querySelector('.st__infinite-container');
        if (currentContainer && newContainer.innerHTML.trim()) {
          currentContainer.insertAdjacentHTML('beforeend', newContainer.innerHTML);
        }

        const paginationContainer = document.querySelector('.st__infinite-trigger');
        if (paginationContainer && newPagination) {
          paginationContainer.innerHTML = newPagination.innerHTML;
        } else {
          break;
        }

        await new Promise((r) => setTimeout(r, 300));

        window.ajaxinatePage++;
        this.dispatchEvent('collection:updated');

        const targetProduct = document.querySelector(
          `[data-identify-product-handle="${state.productHandle}"]`
        );

        if (targetProduct) {
          await new Promise(r => requestAnimationFrame(r));
          this.scrollToProduct(targetProduct);
          this.finishRestoring();
          return;
        }

        const productCountAfter = document.querySelectorAll('.product-item').length;
        if (productCountAfter === productCountBefore) break;

        attempts++;
      } catch {
        break;
      }
    }

    window.scrollTo({ top: state.scrollY, behavior: 'instant' });
    this.finishRestoring();
  }

  scrollToProduct(element) {
    const headerOffset = 100;
    const offsetPosition =
      element.getBoundingClientRect().top + window.pageYOffset - headerOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'instant'
    });

    element.style.transition = 'outline 0.3s ease';
    element.style.outline = '3px solid rgba(0, 123, 255, 0.5)';
    setTimeout(() => (element.style.outline = ''), 2000);
  }

  getCurrentPage() {
    const productsPerPage = 20;
    const totalProducts = document.querySelectorAll('.product-item').length;
    return Math.ceil(totalProducts / productsPerPage);
  }

  extractHandle(url) {
    const match = url.match(/\/products\/([^?/#]+)/);
    return match ? match[1] : null;
  }

  showPreloader() {
    const preloader = document.createElement('div');
    preloader.id = 'scroll-restore-preloader';
    preloader.innerHTML = `
      <div style="
        position: fixed;
        inset: 0;
        background: rgba(255,255,255,0.95);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9999;
        flex-direction: column;
        gap: 1rem;
      ">
        <div style="
          border: 4px solid #f3f3f3;
          border-top: 4px solid #3498db;
          border-radius: 50%;
          width: 50px;
          height: 50px;
          animation: spin 1s linear infinite;
        "></div>
        <p style="font-size: 1rem; color: #333;">Picking up where you left off...</p>
      </div>
      <style>
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      </style>
    `;
    document.body.appendChild(preloader);
  }

  hidePreloader() {
    const preloader = document.getElementById('scroll-restore-preloader');
    if (!preloader) return;

    preloader.style.opacity = '0';
    preloader.style.transition = 'opacity 0.3s ease';
    setTimeout(() => preloader.remove(), 300);
  }
}

// Prevent ajaxinate button from ever navigating
document.addEventListener('click', function(e) {
  const ajaxButton = e.target.closest('.st__infinite-trigger a[href*="page="]');
  if (ajaxButton) {
    e.preventDefault();
  }
}, true);

// Initialize only on collection pages
if (document.querySelector('.template-collection')) {
  window.collectionScrollMemory = new CollectionScrollMemory();
}